import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import type { DepartmentComparisonData } from "@/lib/api";

type SortKey = "department" | "headcount" | "activeCount" | "avgTenureMonths" | "totalLeaveDays" | "avgGross";
type SortDir = "asc" | "desc";

interface Props {
  data: DepartmentComparisonData["departments"];
  isLoading?: boolean;
}

export function DepartmentComparison({ data, isLoading }: Props) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>("headcount");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = [...data].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === "string" && typeof bv === "string") {
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sortDir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="ml-1 text-xs opacity-50">
      {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("analytics.departmentComparison")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("analytics.departmentComparison")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8 px-6">{t("analytics.noData")}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap"
                    onClick={() => handleSort("department")}
                  >
                    Department <SortIcon col="department" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right"
                    onClick={() => handleSort("headcount")}
                  >
                    Headcount <SortIcon col="headcount" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right"
                    onClick={() => handleSort("activeCount")}
                  >
                    Active <SortIcon col="activeCount" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right whitespace-nowrap"
                    onClick={() => handleSort("avgTenureMonths")}
                  >
                    Avg Tenure <SortIcon col="avgTenureMonths" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right whitespace-nowrap"
                    onClick={() => handleSort("totalLeaveDays")}
                  >
                    Leave Days (YTD) <SortIcon col="totalLeaveDays" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-right whitespace-nowrap"
                    onClick={() => handleSort("avgGross")}
                  >
                    Avg Gross <SortIcon col="avgGross" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((dept) => (
                  <TableRow key={dept.department}>
                    <TableCell className="font-medium">{dept.department}</TableCell>
                    <TableCell className="text-right">{dept.headcount}</TableCell>
                    <TableCell className="text-right">{dept.activeCount}</TableCell>
                    <TableCell className="text-right">
                      {dept.avgTenureMonths > 0
                        ? `${dept.avgTenureMonths.toFixed(1)} mo`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {dept.totalLeaveDays > 0 ? dept.totalLeaveDays.toFixed(0) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {dept.avgGross > 0 ? dept.avgGross.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
